import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { map } from 'rxjs/internal/operators/map';
import { CommonDataService } from '../appDataServices/commonDataService';
import { StringConstants } from '../_constants/stringConstants';
import { AuthenticationService, DataService, UtilityService } from '../_services';
import { CommonService } from '../_services/common.service';
import { CurrencyDisplayService } from '../_services/currency-display.service';
import { DateUtilsService } from '../_services/dateUtils';
import { FilterService } from '../_services/filter.service';
import { StorageService } from '../_services/storage.service';
import { Config } from '../_store';

@Injectable({
  providedIn: 'root'
})
export class TransactionsService {

  private seller: string = 'LABELS.SELLERS';
  private buyers: string = 'LABELS.BUYER(S)';
  private tranNo: string = 'LABELS.TRAN_NO';
  private tranNumberSearch: string = 'LABELS.TRAN_NO_SEARCH';
  private status: string = 'LABELS.STATUS';

  transactionData: any = { maxDate: 0, };
  private preserveTransactionFilters = false;

  constructor(
    private authenticationService: AuthenticationService,
    private commonDataService: CommonDataService,
    private dateUtils: DateUtilsService,
    private filtersService: FilterService,
    private dataService: DataService,
    private utilityService: UtilityService,
    private commonService: CommonService,
    private storageService: StorageService,
    private translate: TranslateService,

    private currencyDispalyService: CurrencyDisplayService
  ) {
    this.authenticationService.currentUser.subscribe(x => {
      if (!x) {
        this.initData();
      }
    });
  }

  private initData() {
    this.transactionData = {
      maxDate: 0,
    };
    this.setPreserveTransactionFiltersFlag(false);
  }

  setPreserveTransactionFiltersFlag(flagToSet = true) {
    this.preserveTransactionFilters = flagToSet;
  }

  getTransactions(options: any = {}) {
    return this.commonDataService.getList(
      this.commonDataService.getRequestWithLastAccessTime(options.hardRefresh ? 0 : this.transactionData.maxDate), 'GETTRANSACTIONS',
      (data) => {
        this.transactionData = this.enhanceTransactionResponse(data);
        this.transactionData.maxDate = data.serverTime;
        return this.transactionData;
      });
  }

  private enhanceTransactionResponse(objData): any {
    const localObjData = this.transactionData;
    const currentPayList = localObjData.payHistoryList || [];
    const todayDate = this.dateUtils.getToday();
    const uiConfig = this.authenticationService.currentUserValue?.uiConfig || {};
    const statusDescriptionMap = uiConfig.statusDescriptions || {};
    const objMap = {};
    const sellerMap = {};
    const docTypeMap = {};
    const tranStatusMap = {};
    const buyerMap = {};
    if (currentPayList) {
      currentPayList.forEach(doc => {
        objMap[doc.tranId] = doc;
      });
    }
    const receivedDocs = Object.keys(objData.transactions);
    receivedDocs.forEach(tranId => {
      const doc = objData.transactions[tranId];
      const sellerConfig = objData.sellerConfig[doc.sellerCode];
      this.enhanceSingleTransaction(doc, sellerConfig, todayDate, statusDescriptionMap);
      if (objMap[doc.tranId]) {
        Object.assign(objMap[doc.tranId], doc);
      } else {
        currentPayList.push(doc);
        objMap[doc.tranId] = doc;
      }
    });
    currentPayList.forEach(doc => {
      doc.isVisible = true;
      doc.statusTitle = doc.statusDesc;
      // Temp fix for missing labels
      // if(doc.status == "SUCCESS"){
      //   doc.statusTitle = `LABELS.${(doc.statusDesc).toUpperCase()}`; 
      // }else if(doc.status !== "SUCCESS"){
      //   doc.statusTitle = `LABELS.${(doc.status)}`;      
      // }
      if (!sellerMap[doc.sellerCode]) {
        sellerMap[doc.sellerCode] = doc.sellerName;
      }
      if (!tranStatusMap[doc.statusTitle]) {
        tranStatusMap[doc.statusTitle] = doc.statusTitle;
      }
      if (!buyerMap[doc.buyerNameWithCode]) {
        buyerMap[doc.buyerNameWithCode] = doc.buyerNameWithCode;
      }
    });
    currentPayList.sort((a, b) => b.dtForSort - a.dtForSort);
    const retData: any = {
      payHistoryList: currentPayList,
      sellers: Object.keys(sellerMap).map((sellerCd) => sellerMap[sellerCd]),
      docTypes: Object.keys(docTypeMap).map((docType) => docTypeMap[docType]),
      tranStatusMap: Object.keys(tranStatusMap).map((tranStatus) => tranStatusMap[tranStatus]),
      buyers: Object.keys(buyerMap).map((buyer) => buyerMap[buyer]),
      docCount: currentPayList.length
    };
    retData.filters = this.getTransactionFilters(retData, this.preserveTransactionFilters ? localObjData : {});
    return retData;
  }

  private enhanceSingleTransaction(item, sellerConfig: any = {}, todayDate, statusDescriptionMap) {
    item.sellerName = sellerConfig.sellerNm || '';
    item.statusDesc = statusDescriptionMap[item.status] || item.statusDesc;
    item.showDocBadge = sellerConfig.showInvType;
    item.imgUrl = sellerConfig.sellerImg;
    item.docInfo = {};
    item.currCode = (item.tranAmt || {}).currCode;
    item.bankAmt = (item.tranAmt || {}).bankAmt;
    //item.tranAmt = (item.tranAmt || {}).amt || 0;
    item.formattedAmount = this.dataService.displayNumberAsCurrency(item.tranAmt);
    // Dates
    const tranDateMoment = this.dateUtils.convertDateUnknownFormat(item.tranDate);
    const payDateMoment = this.dateUtils.convertDateUnknownFormat(item.payDate || item.tranDate);
    item.formattedTranDate = this.dateUtils.getFormattedDateForDisplay(tranDateMoment);
    item.formattedPayDate = this.dateUtils.getFormattedDateForDisplay(payDateMoment);
    // Adjustments
    item.tranNo = item.tranNo || item.tranId;
    item.status = item.status || '';
    item.dtForSort = tranDateMoment.toDate().getTime();
    item.freeTxtSearch = (`${item.tranNo}_${item.status}`).toLowerCase();
    const tranStatus = Config.transactionStatusMap[item.status];
    if (!tranStatus) {
      item.bgColor = Config.transactionStatusMap['DEFAULT'].bgColor;
      item.txtColor = Config.transactionStatusMap['DEFAULT'].txtColor;
      item.statusTitle = item.statusDesc || item.status;
    } else {
      item.bgColor = tranStatus.bgColor;
      item.txtColor = tranStatus.txtColor;
      item.statusTitle = item.statusDesc || tranStatus.title;
    }
    item.multiPayBadge = (item.invCnt || 1).toString();
    item.buyerName = item.buyerName || item.buyerCode;
    item.docId = item.tranId;
  }

  private getTransactionFilters(itemData, localTransactionData) {
    const amountProp = 'tranAmtDetails';
    //const isSalesmanLogin = this.authenticationService.isCurrentUserSalesman();
    const sellerObj = {
      company: {
        rules: itemData.sellers.map((sellerName) => {
          return {
            label: sellerName,
            fn: item => item.sellerName === sellerName
          };
        }).sort((a, b) => {
          if (a.label < b.label)
            return -1;
          if (a.label > b.label)
            return 1;
          return 0;
        }),
        label: this.seller,
        amountProp: amountProp
      },
      buyers: {
        rules: itemData.buyers.map((buyerNameWithCode) => {
          return {
            label: buyerNameWithCode,
            fn: item => item.buyerNameWithCode === buyerNameWithCode
          };
        }).sort((a, b) => {
          if (a.label < b.label)
            return -1;
          if (a.label > b.label)
            return 1;
          return 0;
        }),
        label: this.buyers,
        amountProp: amountProp
      },
      status: {
        rules: itemData.tranStatusMap.map(itemStatus => {
          return {
            label: itemStatus,
            fn: item => item.statusTitle === itemStatus
          };
        }),
        label: this.status,
        amountProp: amountProp
      },
      tranNumSearch: {
        isValueFilter: true,
        rules: [{
          label: 'tranNumberSearch',
          fn: (item, val) => {
            return item.tranNo.toLowerCase().indexOf(val.toLowerCase()) !== -1;
          }
        }],
        label: 'tranNumberSearch',
        amountProp: amountProp
      }
    };
    const isSalesmanLogin = this.authenticationService.isCurrentUserSalesman();
    const filtersObj = Object.assign({}, sellerObj);
    return this.filtersService.createFilters(itemData.payHistoryList, filtersObj, 'tranId', localTransactionData.filters);
  }


  enhanceTransactionDetailResponse(response, transactionItem) {
    const currentTransactionDetailsData: any = {};
    const uiConfig = this.authenticationService.currentUserValue?.uiConfig || {};
    const statusDescriptionMap = uiConfig.statusDescriptions;
    const getDateForDisplay = (strDate) => {
      return strDate ? this.dateUtils.getFormattedDateForDisplay(this.dateUtils.convertDateUnknownFormat(strDate)) : ''
    }
    let totalDN = 0;
    let totalInv = 0;
    let totalCD = 0;
    const allCreditNotes = {};
    currentTransactionDetailsData.isInvoicePay = true;
    currentTransactionDetailsData.tranNo = response.tranNo || response.tranId;
    currentTransactionDetailsData.imgUrl = response.sellerImg || response.sellerCode;
    currentTransactionDetailsData.paymentDate = getDateForDisplay(response.payDate);
    currentTransactionDetailsData.approveDate = getDateForDisplay(response.apprvDate);
    const curr = response.currCode;
    currentTransactionDetailsData.approveAmt = this.currencyDispalyService.getFormattedCurrencyStr({amt: response.apprvAmt, curr});
    currentTransactionDetailsData.bankAmount = this.currencyDispalyService.getFormattedCurrencyStr({amt: response.bankAmt, curr});
    currentTransactionDetailsData.formattedCNAmount = this.currencyDispalyService.getFormattedCurrencyStr({amt: response.CNAmt, curr});;
    currentTransactionDetailsData.formattedTotalCDAmount = this.currencyDispalyService.getFormattedCurrencyStr({amt: response.totalCdAmt,curr});
    currentTransactionDetailsData.whtAmt = this.currencyDispalyService.getFormattedCurrencyStr({amt: response.WHT, curr});
    currentTransactionDetailsData.formattedWHTAmount = this.currencyDispalyService.getFormattedCurrencyStr({amt: response.WHT || 0, curr});
    currentTransactionDetailsData.headerStatus = response.headerStatus || '';///`LABELS.${(statusDescriptionMap[response.status]).replaceAll(' ','_').toUpperCase()}` || `LABELS.${(response.headerStatus).replaceAll(' ','_').toUpperCase()}`;   
    if (!response.invoices) {
      response.invoices = [];
    }
    if (!response.invoices.length) {
      if (!response.advPay) {
        response.advPay = {};
      }
      const refNo = response.advPay.refNo || '';
      currentTransactionDetailsData.isInvoicePay = false;
      const transactionType = refNo.split('_').length ? refNo.split('_')[0] : response.payMode;
      response.invoices.push({
        docDate: response.payDate,
        type: transactionType,
        buyerCode: transactionItem.buyerCode,
        docAmt: (response.advPay || {}).amt || response.apprvAmt || 0,
        docApprvAmt: response.apprvAmt,
        bankAmt: response.bankAmt,
        docNo: ((response.advPay || {}).refNo),
        CNAmt: response.payMode === 'CN' ? ((response.advPay || {}).amt) : 0
      });
    }
    currentTransactionDetailsData.invoices = response.invoices.map((item) => {
      item.invDocDate = getDateForDisplay(item.docDate);
      item.invDueDate = getDateForDisplay(item.dueDate);
      // item.invDocAmt = this.dataService.displayNumberAsCurrency(item.docAmt);
      // item.invDocApprvAmt = this.dataService.displayNumberAsCurrency(item.docApprvAmt);
      // item.invCdAmt = this.dataService.displayNumberAsCurrency(item.CDAmt);
      item.invDocAmt = this.currencyDispalyService.getFormattedCurrencyStr({amt: item.docAmt, curr});
      item.invDocApprvAmt = this.currencyDispalyService.getFormattedCurrencyStr({amt: item.docApprvAmt, curr});
      item.invCdAmt = this.currencyDispalyService.getFormattedCurrencyStr({amt: item.CDAmt, curr});
      item.CNAmt = this.currencyDispalyService.getFormattedCurrencyStr({amt: item.CNAmt, curr});
      item.bankAmt = this.currencyDispalyService.getFormattedCurrencyStr({amt: item.bankAmt, curr});

      item.invTds = this.currencyDispalyService.getFormattedCurrencyStr({amt: item.tds, curr});
      item.invScd = this.dataService.displayNumberAsCurrency(item.scd);
      item.bankName = response.bankNm;
      item.sellerCode = response.sellerCode;
      item.docType = item.docType || item.type;
      item.buyerCode = item.buyerCode;
      if (currentTransactionDetailsData.isInvoicePay) {
        totalInv += item.type === "INV" ? 1 : 0;
        totalDN += item.type !== "INV" ? 1 : 0;
      }
      totalCD += item.CDAmt;
      if (!item.creditNotes) {
        item.creditNotes = [];
      }
      item.creditNotes.forEach(crn => {
        allCreditNotes[crn.cnId] = crn;
      });
      item.cnAmount = this.dataService.displayNumberAsCurrency(item.creditNotes.reduce((totalAmt, crn) => { return totalAmt + crn.amt}, 0));
      item.cnCount = item.creditNotes.length;
      if (!item.docViewPath && transactionItem.sellerName === 'Emcure') {
        item.docViewPath = 'http://13.235.178.169/invoices/emcure/SampleSaleInvoice.pdf';
      }
      return item;
    });
    currentTransactionDetailsData.totalCn = Object.keys(allCreditNotes).length;
    if (!currentTransactionDetailsData.isInvoicePay && response.payMode === 'CN') {
      currentTransactionDetailsData.totalCn = ((response.advPay || {}).cnList || []).length;
    }
    const cnAmount = Object.keys(allCreditNotes).map(key => allCreditNotes[key]).reduce((totalAmt, cn) => {
      return totalAmt + cn.amt;
    }, 0);
    currentTransactionDetailsData.cnAmount = this.dataService.displayNumberAsCurrency(cnAmount);
    currentTransactionDetailsData.totalInv = totalInv;
    currentTransactionDetailsData.totalDN = totalDN;
    currentTransactionDetailsData.totalCD = totalCD;
    if (!currentTransactionDetailsData.tranNo) {
      currentTransactionDetailsData.tranNo = transactionItem.tranNo;
    }
    // TODO
    // remove code for hardcoded imgUrl
    delete currentTransactionDetailsData.imgUrl;
    return Object.assign({}, response, transactionItem, currentTransactionDetailsData);
  }

  getTransactionDetailsForCurrentItem(currentItem) {
    const req = {
      transId: currentItem.tranId,
      sellerCode: currentItem.sellerCode
    };
    return this.utilityService.httpPost(req, this.commonService.getInterfaceUrl('GETTRANSACTIONSDETAILS')).pipe(map(data => {
      console.log("in txn service");
      
      console.log(data);
      
      return this.enhanceTransactionDetailResponse(data.data, currentItem);
    }));
  }

  getUploadedRejectionFile(file){
    const req = {
      sellerCode : file.sellerCode,
      buyerCode : file.buyerCode,
      docId : file.docId
    };
    return this.utilityService.httpPost(req, this.commonService.getInterfaceUrl('GET_REJECT_UPLOAD')).pipe(map(data => {
      return data
    }));
  }

  setCurrentTranItem(item) {
    return this.storageService.add(StringConstants.CURRENT_Transaction, item);
  }

  getCurrentTranItem() {
    return this.storageService.get(StringConstants.CURRENT_Transaction);
  }
}
